import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { READINESS_COLORS } from "../../../../constants";
import isEqual from "lodash/isEqual";
import { generateChartArray } from "../../../../utils/readiness-helper";
import ChartTooltip from "./ChartTooltip";
import round from "lodash/round";

const READINESS_NAME = ["Sore", "Stress", "Energy", "Mood", "Sleep", "Score"];
const dateFormat = {
  normal: "YYYY-MM-DD",
  calendar: "MMM D, YYYY",
};

class GraphComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      tooltip: {
        show: false,
        y: 0,
        x: 0,
        content: "",
      },
    };
    this.chartObject = null;
  }

  filterForChart(arr) {
    const { filters } = this.props;
    return arr.map((obj) => {
      const [sore, stress, energy, mood, sleep] = obj.value;
      const updatedReadiness = [
        filters.sore ? sore : 0,
        filters.stress ? stress : 0,
        filters.energy ? energy : 0,
        filters.mood ? mood : 0,
        filters.sleep ? sleep : 0,
      ];
      return { meta: obj.meta, value: updatedReadiness };
    });
  }

  updateChart(newTicks = false) {
    const chartData = generateChartArray(
      this.filterForChart(
        this.props.stats.map((stat) => {
          return { meta: stat.date, value: stat.readiness };
        })
      )
    );

    // avg value calculation
    // commented out this part since avg line is no longer used
    /*
         const avg =
           chartData[0].reduce((prev, acum) => prev + acum.value, 0) /
           chartData[0].length;
         chartData.push(
           chartData[0].map((elem) => {
             return { meta: elem.meta, value: avg };
           })
         );
      */
    if (newTicks)
      this.chartObject.update(
        {
          series: chartData || [],
        },
        {
          ticks: newTicks,
          axisY: {
            low: 0,
            high: newTicks[newTicks.length - 1],
          },
        },
        true
      );
    else
      this.chartObject.update({
        series: chartData || [],
      });
  }

  componentDidUpdate(prevProps) {
    const { series, autoScale = false, stats, filters } = this.props;
    const activeFilters = filters
      ? Object.keys(filters).reduce((acc, value, idx) => {
          if (filters[value]) {
            return acc + 1;
          } else return acc;
        }, 0)
      : false;

    const newTicks = activeFilters
      ? [5, 10, 15, 20, 25].slice(0, activeFilters)
      : false;

    if (
      !isEqual(prevProps.stats, stats) ||
      !isEqual(prevProps.filters, filters)
    ) {
      newTicks && this.updateChart(newTicks);
    } else if (series) {
      if (!isEqual(prevProps.series, series)) {
        const chartData = {
          series,
        };
        let options = {};

        if (autoScale) {
          const isEmpty = series.length && !series[0].length;
          options = {
            axisY: {
              low: 0,
              high: 100,
            },
          };
          if (!isEmpty) {
            const axisY = this.chartObject.options.axisY;
            delete axisY["low"];
            delete axisY["high"];
            options = {
              axisY,
            };
          }
        }
        this.chartObject.update(chartData, options, true);
      }
    }
  }

  setTooltip(props) {
    const { y = 0, x = 0, show = false } = props;
    this.setState({
      tooltip: {
        show,
        x,
        y,
      },
    });
  }

  fillTooltip(target, valueName) {
    const {
      series,
      colors,
      updateHoveredStat,
      tooltipOffset = 0,
      tooltipUnit = "",
      formatTooltip = false,
    } = this.props;
    const date = target.getAttribute("ct:meta");
    if (!date) return;
    const colorsCopy = [...colors];
    // assumes valuable line data is on the last serie
    const lastSerieIndex = series.length - 1;
    const value = series[lastSerieIndex].find((elem) =>
      moment(elem.meta, dateFormat.calendar).isSame(
        moment(date, dateFormat.calendar)
      )
    ).value;
    const colorIndex =
      colorsCopy.length - 1 - (Math.floor(value - tooltipOffset) - 5);

    const style = `style="color: ${colorsCopy[colorIndex]};"`;
    const displayValue = formatTooltip
      ? Math.round(value - tooltipOffset).toLocaleString()
      : value - tooltipOffset;

    document.querySelector("[role=tooltip]").innerHTML = `
              <div role="tooltip-date">${date}</div>
              <div role="tooltip-content">
                <div role="tooltip-row" >
                ${
                  tooltipUnit
                    ? `<span></span>`
                    : `<span>${valueName}</span>&nbsp;`
                }
                  <span ${style}>
                  ${displayValue}${tooltipUnit}
                  </span>
                </div>
              </div>
            `;

    const isLastValue =
      target.getAttribute("ct:value") == series[0][series[0].length - 1]?.value;

    this.setTooltip({
      show: true,
      x: target.getAttribute("x1") - (isLastValue ? 75 : 50),
      y: target.getAttribute("y1") - -5,
    });
    if (updateHoveredStat) updateHoveredStat(date);
  }

  componentDidMount() {
    const {
      series,
      labels,
      ticks = [5, 10, 15, 20, 25],
      chartName,
      colors,
      updateHoveredStat,
      chartOptions = {},
      areaOpacity = 1,
      autoScale = false,
      bodyMeasurement = false,
    } = this.props;

    const Chartist = require("chartist");
    // it changes the render order of the grid and the chart
    (function (window, document, Chartist) {
      "use strict";

      Chartist.plugins = Chartist.plugins || {};
      Chartist.plugins.cgGridOnTop = function () {
        return function cgGridOnTop(chart) {
          if (chart instanceof Chartist.Line || Chartist.Bar) {
            chart.on("created", function () {
              var layerGrid = chart.svg._node.children[0];
              var lastChild = chart.svg._node.children[1];

              lastChild.parentNode.insertBefore(
                layerGrid,
                lastChild.nextSibling
              );
            });
          }
        };
      };
    })(window, document, Chartist);

    const chart = series
      ? series
      : generateChartArray(
          this.props.stats.map((stat) => {
            return { meta: stat.date, value: stat.readiness };
          }) || []
        );
    const data = {
      series: chart,
    };

    const options = Object.assign(
      {
        height: this.props.graphHeight || 270,
        ticks: autoScale ? [] : ticks,
        showArea: true,
        showLine: true,
        showPoint: true,
        fullWidth: true,
        lineSmooth: false,
        chartPadding: { left: -15, bottom: -20, right: -5 },
        axisX: {
          showGrid: false,
        },
        axisY: {
          labelOffset: {
            y: 5,
            x: 5,
          },
          onlyInteger: true,
        },
        //   plugins: [Chartist.plugins.cgGridOnTop()],
      },
      {
        axisY: autoScale
          ? {
              labelOffset: {
                x: 5,
              },
            }
          : {
              low: 0,
              high: 25,
              onlyInteger: true,
              labelOffset: {
                x: 5,
              },
            },
        series: { fillColor: "red" },
      },
      chartOptions
    );

    this.chartObject = new Chartist.Line(
      this.props.graphId ? `#${this.props.graphId}` : ".graph",
      data,
      options
    ).on("draw", (data) => {
      if (data.type === "area") {
        if (colors) {
          if (data.seriesIndex >= 0 && data.seriesIndex < 6) {
            if (
              (chartName === "KG" || chartName === "LB") &&
              data.seriesIndex === 0
            ) {
              // bodyMeasurement
              //   ? data.element.attr({
              //       style: "fill: var(--gradient-left) ",
              //     })
              // :
              data.element.attr({
                style: `fill: rgba(0, 175, 228, ${areaOpacity})`,
              });
            } else {
              bodyMeasurement
                ? data.element.attr({
                    style: `fill: rgba(0, 175, 228, ${areaOpacity})`,
                  })
                : data.element.attr({
                    style: `fill: ${
                      colors[data.seriesIndex]
                    }; fill-opacity: ${areaOpacity};`,
                  });
            }
          }
          if (data.seriesIndex === 6) {
            data.element.attr({
              style: "stroke: #FFFFFF; stroke-width: 0px; ",
            });
          }
          if (data.seriesIndex === 7) {
            data.element.attr({
              style:
                "stroke: #FFFFFF; stroke-width: 0px; fill: rgba(255, 255, 255, .3)",
            });
          }
        } else {
          if (data.seriesIndex === 0) {
            data.element.attr({
              style: `fill: url(#${this.props.sectionClassNames}-sleep-gradient);`,
            });
          }
          if (data.seriesIndex === 1) {
            data.element.attr({
              style: `fill: url(#${this.props.sectionClassNames}-mood-gradient);`,
            });
          }
          if (data.seriesIndex === 2) {
            data.element.attr({
              style: `fill: url(#${this.props.sectionClassNames}-energy-gradient);`,
            });
          }
          if (data.seriesIndex === 3) {
            data.element.attr({
              style: `fill: url(#${this.props.sectionClassNames}-stress-gradient);`,
            });
          }
          if (data.seriesIndex === 4) {
            data.element.attr({
              style: `fill: url(#${this.props.sectionClassNames}-sore-gradient);`,
            });
          }
          // average line custom styling
          if (data.seriesIndex === 5) {
            data.element.removeClass("ct-area");
            data.element.addClass("ct-line");
            data.element.attr({
              style: `stroke-width: 0px; stroke: #fff;`,
            });

            // update the path to be drawn in order to display the horizontal line
            const d = data.element._node.getAttribute("d").split(",");
            d[1] = d[2];
            d[1].split("L")[0];
            d[d.length - 1] = d[1].split("L")[0];
            d.toString();
            data.element._node.setAttribute("d", d);
          }
        }
      }
      if (data.type === "line" && !bodyMeasurement) {
        if (colors) {
          if (data.seriesIndex >= 0 && data.seriesIndex < 6) {
            if (chartName === "RPE") {
              if (data.seriesIndex === 0) {
                data.element.attr({
                  style: `stroke-width: 0px;`,
                });
              }
            } else {
              data.element.attr({
                style: `stroke: ${colors[data.seriesIndex]}; stroke-width: ${
                  bodyMeasurement ? "1" : "3"
                }px;`,
              });
            }
          }
          if (data.seriesIndex === 6) {
            data.element.attr({
              style: "stroke: rgba(0, 0, 0, .3); stroke-width: 0px;",
            });
          }
          if (data.seriesIndex === 7) {
            data.element.attr({
              style: "stroke: #FFFFFF; stroke-width: 3px;",
            });
          }
        } else {
          if (data.seriesIndex === 0) {
            data.element.attr({
              style: `stroke: ${READINESS_COLORS.sleep}; stroke-width: ${
                this.props.filters.sleep ? "2px" : "0px"
              }`,
            });
          }
          if (data.seriesIndex === 1) {
            data.element.attr({
              style: `stroke: ${READINESS_COLORS.mood}; stroke-width: ${
                this.props.filters.mood ? "2px" : "0px"
              }`,
            });
          }
          if (data.seriesIndex === 2) {
            data.element.attr({
              style: `stroke: ${READINESS_COLORS.energy}; stroke-width: ${
                this.props.filters.energy ? "2px" : "0px"
              }`,
            });
          }
          if (data.seriesIndex === 3) {
            data.element.attr({
              style: `stroke: ${READINESS_COLORS.stress}; stroke-width: ${
                this.props.filters.stress ? "2px" : "0px"
              }`,
            });
          }
          if (data.seriesIndex === 4) {
            data.element.attr({
              style: `stroke: ${READINESS_COLORS.sore}; stroke-width: ${
                this.props.filters.sore ? "2px" : "0px"
              }`,
            });
          }
        }
      }
      if (data.type === "line" && bodyMeasurement) {
        data.element.attr({
          style:
            "stroke: var(--gradient-left); stroke-width: 2px; stroke-dasharray:0",
        });
      }
      //   if (data.type === "line") {
      //     if (colors) {
      //       if (data.seriesIndex >= 0 && data.seriesIndex < 6) {
      //         if (chartName === "RPE") {
      //           if (data.seriesIndex === 0) {
      //             data.element.attr({
      //               style: `stroke-width: 0px;`,
      //             });
      //           }
      //         } else {
      //           data.element.attr({
      //             style: `stroke: ${colors[data.seriesIndex]}; stroke-width: ${
      //               bodyMeasurement ? "1" : "3"
      //             }px;`,
      //           });
      //         }
      //       }
      //       if (data.seriesIndex === 6) {
      //         data.element.attr({
      //           style: "stroke: rgba(0, 0, 0, .3); stroke-width: 0px;",
      //         });
      //       }
      //       if (data.seriesIndex === 7) {
      //         data.element.attr({
      //           style: "stroke: #FFFFFF; stroke-width: 3px;",
      //         });
      //       }
      //     }
      //   }
      if (data.type === "grid") {
        data.element.attr({
          style: "stroke: var(--white-10); stroke-dasharray: 5px 5px;",
        });
      }
      if (data.type === "label") {
        data.element.attr({
          style: "color: var(--white-38); font-size: 12px; margin-top: 2px;",
        });
      }
      if (data.type === "point") {
        if (chartName === "MS") {
          data.element.attr({
            style: "stroke: var(--primary); opacity: 1; stroke-width: 0.75rem;",
          });
        }
        try {
          data.element._node.addEventListener("mouseenter", ({ target }) => {
            if (series) {
              this.fillTooltip(
                target,
                bodyMeasurement ? bodyMeasurement : "RPE"
              );
            } else {
              const date = target.getAttribute("ct:meta");
              const rawReadiness = this.props.stats.find(
                (elem) => date === elem.date
              ).readiness;
              const total = rawReadiness.reduce((prev, acum) => prev + acum, 0);
              const readinessArr = rawReadiness.map(
                (elem, i) =>
                  `<div role="tooltip-row">
                          <span role="${READINESS_NAME[
                            i
                          ].toLowerCase()}-label">${READINESS_NAME[i]}</span>
                          <span role="${READINESS_NAME[
                            i
                          ].toLowerCase()}-value">${elem}</span>
                      </div>`
              );
              document.querySelector("[role=tooltip]").innerHTML = `
                <div role="tooltip-date">${date}</div>
                 <div role="tooltip-content">
                  ${readinessArr.join("")}
                  <div role="tooltip-row">
                    <span role="${READINESS_NAME[5].toLowerCase()}-label">${
                READINESS_NAME[5]
              }</span>
                    <span role="${READINESS_NAME[5].toLowerCase()}-value">${total}</span>
                  </div>
                  </div>
              `;
              this.setTooltip({
                show: true,
                x: target.getAttribute("x1") - 50,
                y: target.getAttribute("y1") - -5,
              });
            }
          });
        } catch (error) {}
        data.element._node.addEventListener("mouseleave", () => {
          this.setTooltip({
            show: false,
          });
          if (updateHoveredStat) updateHoveredStat();
        });
      }
    });
  }

  renderLeyend() {
    const LeyendItem = ({ item }) => (
      <div className="leyend-item">
        <div
          className="color"
          style={{ background: READINESS_COLORS[item.toLowerCase()] }}
        />
        <i>{item}</i>
      </div>
    );
    return (
      <div className="leyend-container">
        <LeyendItem item="Sore" />
        <LeyendItem item="Stress" />
        <LeyendItem item="Energy" />
        <LeyendItem item="Mood" />
        <LeyendItem item="Sleep" />
        <div className="leyend-item">
          <div className="avg-bar" />
          <i>Avg</i>
        </div>
      </div>
    );
  }

  renderAvgText(avg) {
    return (
      <hgroup>
        <p className="s1">Average </p>
        <span>{typeof avg === "number" ? round(avg, 1) : avg}</span>
      </hgroup>
    );
  }

  renderChart() {
    const { dateFrom, dateTo, labels, bodyClass } = this.props;
    const { tooltip } = this.state;

    return (
      <div
        role="graph-body"
        className={`${bodyClass || ""}`}
        style={{ position: "relative" }}
      >
        <div
          {...(this.props.graphId
            ? { id: this.props.graphId }
            : { className: "graph" })}
        />
        <ChartTooltip
          style={
            tooltip.show
              ? {
                  left: tooltip.x,
                  top: tooltip.y,
                }
              : {
                  display: "none",
                }
          }
        />

        <div role="graph-dates">
          <span>{labels ? labels[0] : dateFrom}</span>
          <span>
            {labels
              ? labels[labels.length - 1]
              : dateTo ||
                moment(new Date(), dateFormat.normal).format(
                  dateFormat.calendar
                )}
          </span>
        </div>
      </div>
    );
  }

  render() {
    const chartName = this.props.chartName || "Score";
    const {
      displayLeyend = true,
      headerRight,
      displayAvgText = false,
      sectionClassNames = "",
    } = this.props;
    const leyend = displayLeyend ? this.renderLeyend() : <div></div>;
    const avgText = displayAvgText ? (
      this.renderAvgText(displayAvgText)
    ) : (
      <div></div>
    );
    const { isComposition } = this.props;
    const rendertitle = () => (
      <div role="graph-title">
        <div role="graph-header">
          <p className="s1">{chartName}</p>
          {headerRight && (
            <hgroup>
              <p className="s1">{headerRight.name} </p>
              <span>{headerRight.value}</span>
            </hgroup>
          )}
        </div>
        {avgText ? avgText : leyend}
      </div>
    );

    const verticalGradient = (color, id, percentage) => {
      return (
        <svg
          style={{ width: 0, height: 0, position: "absolute" }}
          aria-hidden="true"
          focusable="false"
        >
          <linearGradient id={`${sectionClassNames}-${id}`} x2="0" y2="1">
            <stop offset="0%" stop-color={`${color}ee`} />

            <stop offset={percentage} stop-color={`${color}00`} />
          </linearGradient>
        </svg>
      );
    };
    const horizontalGradient = (colorStart, colorEnd, id, percentage) => {
      return (
        <svg
          style={{ width: 0, height: 0, position: "absolute" }}
          aria-hidden="true"
          focusable="false"
        >
          <linearGradient id={`${sectionClassNames}-${id}`} x2="1" y2="0">
            <stop offset="0%" stop-color={`${colorStart}`} />

            <stop offset={percentage} stop-color={`${colorEnd}`} />
          </linearGradient>
        </svg>
      );
    };

    return (
      <section role="graph-wrapper" className={sectionClassNames}>
        {verticalGradient(READINESS_COLORS.sleep, "sleep-gradient", "60%")}
        {verticalGradient(READINESS_COLORS.mood, "mood-gradient", "70%")}
        {verticalGradient(READINESS_COLORS.energy, "energy-gradient", "80%")}
        {verticalGradient(READINESS_COLORS.stress, "stress-gradient", "90%")}
        {verticalGradient(READINESS_COLORS.sore, "sore-gradient", "100%")}
        {this.props.bodyMeasurement &&
          horizontalGradient("#00afe477", "#5bc50077", "body-gradient", "100%")}
        {this.props.bodyMeasurement &&
          horizontalGradient(
            "#00afe4ff",
            "#5bc500ff",
            "body-gradient-stroke",
            "100%"
          )}
        {!isComposition && rendertitle()}
        {this.renderChart()}
      </section>
    );
  }
}

GraphComponent.propTypes = {
  chartName: PropTypes.string,
  dateFrom: PropTypes.string,
  dateTo: PropTypes.string,
  stats: PropTypes.array,
  filters: PropTypes.object,
  series: PropTypes.array,
  labels: PropTypes.array,
  colors: PropTypes.array,
  chartOptions: PropTypes.object,
  displayLeyend: PropTypes.bool,
  updateHoveredStat: PropTypes.func,
  areaOpacity: PropTypes.bool,
  bodyMeasurement: PropTypes.string,
  tooltipUnit: PropTypes.string,
  autoScale: PropTypes.bool,
  displayAvgText: PropTypes.bool,
};

export default GraphComponent;
